/**
 * /payroll-l2/differentials — Shift, hazard, and height pay differential management.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/differentials")({
  component: DifferentialsPage,
});

interface Differential {
  id: string;
  code: string;
  name: string;
  multiplier: string;
  is_active: boolean;
}

interface DifferentialForm {
  code: string;
  name: string;
  multiplier: string;
}

function blankForm(): DifferentialForm {
  return { code: "", name: "", multiplier: "1.0" };
}

function DifferentialsPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [form, setForm] = useState<DifferentialForm>(blankForm());
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["payroll-l2", "differentials"],
    queryFn: async () => api<Differential[]>("/api/payroll-l2/differentials?active_only=false"),
  });

  const createMut = useMutation({
    mutationFn: async () =>
      api("/api/payroll-l2/differentials", {
        method: "POST",
        body: { code: form.code, name: form.name, multiplier: form.multiplier },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "differentials"] });
      setCreateOpen(false);
      setForm(blankForm());
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Failed to create"),
  });

  const toggleMut = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) =>
      api(`/api/payroll-l2/differentials/${id}`, {
        method: "PATCH",
        body: { is_active: !is_active },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-l2", "differentials"] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/payroll-l2/differentials/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-l2", "differentials"] }),
  });

  const set = (k: keyof DifferentialForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="h5" fontWeight={600}>Differentials</Typography>
          <IconButton
            size="small"
            onClick={() => setInfoOpen(true)}
            aria-label="How differentials work"
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Box sx={{ ml: 1 }}>
            <HelpHint docPath="payroll-l2/differential-stacking.md" label="Differential stacking help" />
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Differential
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Shift, hazard, and height pay multipliers applied on top of base craft rates.
      </Typography>

      {isLoading && <CircularProgress size={24} />}
      {loadError && <Alert severity="error">Failed to load differentials.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Multiplier</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{d.code}</TableCell>
                <TableCell>{d.name}</TableCell>
                <TableCell>{Number(d.multiplier).toFixed(3)}×</TableCell>
                <TableCell>
                  <Chip
                    label={d.is_active ? "Active" : "Inactive"}
                    color={d.is_active ? "success" : "default"}
                    size="small"
                    onClick={() => toggleMut.mutate({ id: d.id, is_active: d.is_active })}
                    clickable
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => deleteMut.mutate(d.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No differentials configured. Add a shift or hazard pay multiplier to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Differential</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Code"
              value={form.code}
              onChange={set("code")}
              placeholder="e.g. NIGHT, HAZARD, HEIGHT"
              inputProps={{ style: { textTransform: "uppercase" } }}
              required
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Night Shift Premium"
              required
            />
            <TextField
              label="Multiplier"
              type="number"
              value={form.multiplier}
              onChange={set("multiplier")}
              helperText="1.0 = no premium, 1.15 = 15% premium"
              inputProps={{ step: "0.001", min: "1.0" }}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setForm(blankForm()); setError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!form.code || !form.name || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info dialog — explains how differentials stack */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>How differentials work</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            A <b>differential</b> is a pay premium expressed as a multiplier
            on the base craft rate — e.g. NIGHT = 1.15 means a 15% premium
            for night-shift hours.
          </Typography>
          <Typography variant="body2" paragraph>
            <b>How a differential applies to a labor event:</b>
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0, mb: 2 }}>
            <li>
              <Typography variant="body2">
                Assign a differential to an <b>employee</b> (always applies
                to their hours) or to a <b>project</b> (applies to anyone
                working on that project), with effective-from / effective-to
                dates.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                When a labor event is processed, we collect every active
                differential that matches the employee + project on the
                work date.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                If the same differential is attached to both the employee
                and the project, it is counted <b>once</b>.
              </Typography>
            </li>
          </Box>

          <Typography variant="body2" paragraph>
            <b>How multiple differentials combine</b> is set per-project
            (Project → "Differential stacking"), because most contractors
            run a mix of union and non-union jobs:
          </Typography>

          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Compound (union default)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
              NIGHT (1.15) × HAZARD (1.25) = <b>1.4375</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Each premium is "in addition to" the others, applied to the
              resulting wage. Matches most US construction union contracts
              (carpenters, IBEW, operating engineers).
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Additive
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
              NIGHT (+15%) + HAZARD (+25%) on base = <b>1.40</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Premiums sum on top of base, no compounding interaction. Some
              prevailing-wage contracts and certain company policies write
              it this way. Slightly under-pays vs. compound.
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max (typical non-union)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
              NIGHT (1.15) + HAZARD (1.25) → <b>1.25</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Only the largest single premium counts — "no double-dipping."
              Common in non-union shops and at-will employers.
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mt: 1 }}>
            Default for new projects is <b>compound</b>. Change a project's
            stacking mode on its detail page when you set up a non-union
            job.
          </Alert>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Full guide: <code>docs/help/payroll-l2/differential-stacking.md</code> ·
            Or open the chat (⌘J) and ask Bynaus for examples.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
